/**
 * @fileoverview Checks a Calc AST for possible semantic errors/warnings.
 * This can include things like mis-matched types, invalid column references, and more.
 */

import { closest } from "fastest-levenshtein";
import { minBy } from "lodash";

import {
  CalcsDefinition,
  normalizeCalcsDefinition,
} from "../display-table/calcTypes.js";
import { notNullish } from "../filterGuards.js";
import { setDifference } from "../utils/setUtils.js";

import { getAllNodesOfType } from "./calcAstUtils.js";
import { CalcColumnContext, CalcParamContext } from "./CalcColumnContext.js";
import {
  CalcParseError,
  CalcParseResult,
  parseCalcExpression,
} from "./CalcParser.js";
import { sortCalcsTopologically } from "./calcSortUtils.js";
import {
  CalcType,
  CalcTypeChecker,
  CalcTypeCheckerAggregationMode,
} from "./CalcTypeChecker.js";

export interface CalcValidationOptions {
  includeWarnings?: boolean;
  aggregationMode?: CalcTypeCheckerAggregationMode;
  disableHqlNextOnlyFunctions?: boolean;
}

export interface CalcValidationResult {
  severity: "error" | "warning";
  message: string;
  selfStart: number;
  selfEnd: number;
}

export class CalcValidator {
  private typeChecker: CalcTypeChecker;
  private opts: CalcValidationOptions;

  // eslint-disable-next-line max-params -- five params is fine
  constructor(
    private root: CalcParseResult,
    private columnContext: CalcColumnContext,
    private paramContext: CalcParamContext,
    private otherCalcs: ReadonlyMap<string, CalcType>,
    opts?: CalcValidationOptions,
  ) {
    this.opts = opts ?? {
      includeWarnings: true,
      aggregationMode: "ungroupable",
      disableHqlNextOnlyFunctions: true,
    };
    this.typeChecker = new CalcTypeChecker(
      this.root,
      this.columnContext,
      this.paramContext,
      otherCalcs,
      this.opts.aggregationMode,
      this.opts.disableHqlNextOnlyFunctions,
    );
  }

  public validate(): CalcValidationResult[] {
    return [
      ...this.validateColumnReferences(),
      ...this.validateParameterReferences(),
      ...this.checkTypes(),
    ];
  }

  public getRootType(): CalcType {
    return this.typeChecker.getRootType();
  }

  private validateColumnReferences(): CalcValidationResult[] {
    const errors: CalcValidationResult[] = [];

    const columnRefs = getAllNodesOfType(this.root, "column");
    const columnIds = [
      ...this.columnContext.map((c) => c.columnId),
      ...this.otherCalcs.keys(),
    ];
    const lowercaseColumnIdMap = new Map(
      columnIds.map((id) => [id.toLowerCase(), id] as const),
    );
    const columnIdsSet: Set<string> = new Set(columnIds);

    for (const columnRef of columnRefs) {
      if (!columnIdsSet.has(columnRef.name)) {
        let message = `Unknown column "${columnRef.name}"`;
        if (columnIds.length > 0) {
          const closestLowercaseColumnId = closest(
            columnRef.name.toLowerCase(),
            [...lowercaseColumnIdMap.keys()],
          );
          const closestColumnId = lowercaseColumnIdMap.get(
            closestLowercaseColumnId,
          );

          // should never be `null`, but check just to be safe
          if (closestColumnId != null) {
            message += `. Did you mean "${closestColumnId}"?`;
          }
        }

        errors.push({
          severity: "error",
          message,
          selfStart: columnRef.selfStart,
          selfEnd: columnRef.selfEnd,
        });
      }
    }

    return errors;
  }

  private validateParameterReferences(): CalcValidationResult[] {
    const errors: CalcValidationResult[] = [];

    const paramRefs = getAllNodesOfType(this.root, "parameterReference");
    const paramNameSet = new Set(
      Object.entries(this.paramContext)
        .map(([name, type]) => (type != null ? name : undefined))
        .filter(notNullish),
    );

    for (const paramRef of paramRefs) {
      if (!paramNameSet.has(paramRef.name)) {
        const message = `Unknown variable reference "${paramRef.name}"`;

        errors.push({
          severity: "error",
          message,
          selfStart: paramRef.selfStart,
          selfEnd: paramRef.selfEnd,
        });
      }
    }

    return errors;
  }

  private checkTypes(): CalcValidationResult[] {
    const typeErrors = this.typeChecker.getTypeErrors();
    return typeErrors.map((e) => ({
      severity: "error",
      ...e,
    }));
  }
}

/**
 * Convenience function for validating an AST in one easy function call
 * @param root Expression to validate
 * @param columnContext List of base underlying columns
 * @param otherCalcs Map of other calc expressions which may also be referenced in addition to `columnContext`
 */
export function validateCalcAst(
  root: CalcParseResult,
  columnContext: CalcColumnContext,
  paramContext: CalcParamContext,
  otherCalcs: ReadonlyMap<string, CalcType> = new Map(),
  opts?: CalcValidationOptions,
): CalcValidationResult[] {
  return new CalcValidator(
    root,
    columnContext,
    paramContext,
    otherCalcs,
    opts,
  ).validate();
}

/**
 * Convenience function for validating an group of calc ASTs all at once.
 * Useful for when calcs are allowed to reference each other -
 * this function will validate that they don't contain circular references
 * and be smart enough to use the inferred type of each dependent calc during validation.
 */
export function validateAllCalcs(
  calcs: ReadonlyMap<string, CalcParseResult>,
  columnContext: CalcColumnContext,
  paramContext: CalcParamContext,
  opts?: CalcValidationOptions,
): {
  errors: Map<string, CalcValidationResult[]>;
  resultTypes: Map<string, CalcType>;
  sortedCalcs: string[];
} {
  const results = new Map<string, CalcValidationResult[]>();

  const calcsToSort = Array.from(calcs.entries()).map(([alias, expr]) => ({
    alias,
    expr,
  }));
  const sortResult = sortCalcsTopologically(calcsToSort);

  let goodAliases: Set<string>;
  if (sortResult.type === "success") {
    goodAliases = new Set(sortResult.result.map((c) => c.alias));
  } else {
    goodAliases = new Set(sortResult.partialResult?.map((c) => c.alias) ?? []);

    const badAliases = setDifference(calcs.keys(), goodAliases);
    for (const alias of badAliases) {
      const badCalc = calcs.get(alias)!;

      const badCalcReferences = getAllNodesOfType(badCalc, "column");
      const badRef = badCalcReferences.find((ref) => badAliases.has(ref.name));

      results.set(alias, [
        {
          severity: "error",
          message: sortResult.reason,
          selfStart: badRef?.selfStart ?? badCalc.start,
          selfEnd: badRef?.selfEnd ?? badCalc.end,
        },
      ]);
    }
  }

  // Go through calcs without circular references one-by-one in order,
  // validating them and getting result type to use in other validations
  const calcTypes: Map<string, CalcType> = new Map();
  for (const alias of goodAliases) {
    const validator = new CalcValidator(
      calcs.get(alias)!,
      columnContext,
      paramContext,
      calcTypes,
      opts,
    );
    results.set(alias, validator.validate());
    calcTypes.set(alias, validator.getRootType());
  }

  return {
    errors: results,
    resultTypes: calcTypes,
    sortedCalcs: Array.from(goodAliases),
  };
}

export function validateAllCalcsFromText(
  calcs: ReadonlyMap<string, string>,
  columnContext: CalcColumnContext,
  paramContext: CalcParamContext,
  opts?: CalcValidationOptions,
): {
  errors: Map<string, Array<CalcParseError | CalcValidationResult>>;
  resultTypes: Map<string, CalcType>;
  sortedCalcs: string[];
} {
  const asts = new Map(
    [...calcs.entries()].map(([alias, text]) => [
      alias,
      parseCalcExpression(text),
    ]),
  );

  const {
    errors,
    resultTypes,
    sortedCalcs,
  }: {
    errors: Map<string, Array<CalcParseError | CalcValidationResult>>;
    resultTypes: Map<string, CalcType>;
    sortedCalcs: string[];
  } = validateAllCalcs(asts, columnContext, paramContext, opts);

  // If there was a parse error, overwrite any more advanced validation results and just use the parse error
  for (const [alias, ast] of asts) {
    const parseErrors = getAllNodesOfType(ast, "error");
    const firstError = minBy(parseErrors, (err) => err.selfStart);
    if (firstError != null) {
      errors.set(alias, [firstError]);
    }
  }

  return { errors, resultTypes, sortedCalcs };
}

export function validateCalcDefinition(
  calcs: CalcsDefinition,
  opts?: CalcValidationOptions &
    Partial<{ overrideColumnContext: CalcColumnContext }>,
): ReturnType<typeof validateAllCalcsFromText> {
  const normalCalcs = normalizeCalcsDefinition(calcs);
  const calcMap = new Map(
    normalCalcs.calcColumns.map((c) => [c.name, c.expression]),
  );
  const calcColumnContext: CalcColumnContext =
    opts?.overrideColumnContext ?? normalCalcs.referencedColumns;
  const calcParamContext: CalcParamContext =
    normalCalcs.referencedParamTypes ?? {};
  return validateAllCalcsFromText(
    calcMap,
    calcColumnContext,
    calcParamContext,
    opts,
  );
}

export function isCalcErrorFatal(
  error: CalcParseError | CalcValidationResult,
): error is CalcParseError {
  return (
    ("severity" in error && error.severity === "error") ||
    ("type" in error && error.type === "error")
  );
}
